/**
 * EmployerSignalsPage.jsx — RC EMPLOYER.IA.SLICE-1.
 *
 * Honest roadmap-oriented surface at `/employer/signals`.
 *
 * STRICT OPERATIONAL HONESTY:
 *   • No fake inbox entries. No fabricated counts. No claim of
 *     production capability.
 *   • This page is the FUTURE home of the employer signal inbox —
 *     where candidates' responses to your interest signals will
 *     surface once the bi-directional flow is publicly enabled.
 *
 * Today the candidate-controlled response lifecycle exists
 * end-to-end (candidate can accept / decline / defer), but the
 * employer entry point for sending signals is operator-only-
 * gated inside `/employer/roles/{id}/matches`. Until that gate
 * opens broadly, this page is intentionally informational.
 */
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Inbox, Sparkles, ShieldCheck, UserCheck, ArrowRight, Briefcase,
} from 'lucide-react';


const ROADMAP_STAGES = [
  {
    icon: Sparkles,
    title: 'Anonymous matches surface',
    body: (
      'NDORFlow scores candidates against each live role and surfaces '
      + 'role-scoped matches in your workspace — without revealing '
      + 'identity.'
    ),
  },
  {
    icon: Inbox,
    title: 'You send interest signals',
    body: (
      'For candidates worth a closer look, you send a structured '
      + 'expression of interest. PII stays hidden in your view.'
    ),
  },
  {
    icon: UserCheck,
    title: 'Candidates respond on their terms',
    body: (
      'The candidate accepts, declines, or defers. Their identity is '
      + 'shared with you only when they accept, scoped to that role.'
    ),
  },
  {
    icon: ShieldCheck,
    title: 'Responses appear here',
    body: (
      'This page becomes your signal inbox — acceptances unlock '
      + 'approved profile fields; declines and deferrals remain '
      + 'private. Auditable end-to-end.'
    ),
  },
];


export default function EmployerSignalsPage() {
  return (
    <div
      className="mx-auto max-w-3xl py-10 md:py-16 px-4 space-y-8"
      data-testid="employer-signals-page"
    >
      <header className="space-y-3 text-center">
        <p className="text-[10px] uppercase tracking-[0.24em] text-ndor-gold font-medium">
          Signals
        </p>
        <h1 className="text-2xl md:text-3xl font-semibold text-ndor-ink">
          Signals inbox
        </h1>
        <div className="flex items-center justify-center gap-2">
          <Badge
            variant="outline"
            className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px] uppercase tracking-[0.14em] text-ndor-ink-secondary"
            data-testid="employer-signals-preview-pill"
          >
            Available for enabled workspaces
          </Badge>
        </div>
        <p className="text-sm text-ndor-ink-muted leading-relaxed max-w-xl mx-auto">
          NDORFlow Signals is built and live for enabled workspaces.
          Once enabled here, this page collects candidate responses
          to the interest signals you send from a role&apos;s
          NDOR-surfaced matches.
        </p>
      </header>

      <Card
        className="border-ndor-edge bg-ndor-surface"
        data-testid="employer-signals-roadmap-card"
      >
        <CardContent className="px-5 py-5 sm:px-6 sm:py-6 space-y-4">
          <p className="text-xs uppercase tracking-[0.18em] text-ndor-ink-secondary font-medium">
            How the workflow will land
          </p>
          <ol
            className="space-y-3"
            data-testid="employer-signals-roadmap-list"
          >
            {ROADMAP_STAGES.map((s, i) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.title}
                  className="flex items-start gap-3 rounded-md border border-ndor-edge bg-ndor-bg/40 px-4 py-3"
                  data-testid={`employer-signals-roadmap-stage-${i + 1}`}
                >
                  <div
                    className="shrink-0 w-8 h-8 rounded-md border border-ndor-edge bg-ndor-surface-alt/50 flex items-center justify-center"
                    aria-hidden
                  >
                    <Icon className="h-4 w-4 text-ndor-ink-secondary" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-medium text-ndor-ink">
                      {i + 1}. {s.title}
                    </p>
                    <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      <Card
        className="border-ndor-edge bg-ndor-surface"
        data-testid="employer-signals-mvp-card"
      >
        <CardContent className="px-5 py-5 sm:px-6 sm:py-6 space-y-3">
          <p className="text-sm font-medium text-ndor-ink">
            Until then, evidence-led decisions happen in Roles.
          </p>
          <p className="text-xs text-ndor-ink-secondary leading-relaxed">
            Upload candidate dossiers against a live role, run NDOR
            evaluations, capture verdicts, and assemble a defensible
            governance pack. That surface is fully live for your
            workspace today.
          </p>
          <div>
            <Link to="/employer/roles" data-testid="employer-signals-roles-cta">
              <Button
                size="sm"
                className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
              >
                <Briefcase className="mr-2 h-4 w-4" />
                Open Roles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <p
        className="text-[11px] text-ndor-ink-muted text-center leading-relaxed"
        data-testid="employer-signals-footnote"
      >
        Signal acceptance shares only an allow-listed projection of a
        candidate&apos;s profile — name, headline, location, current role,
        summary, top skills. No contact details. No CV download.
        Candidate consent is required for every reveal.
      </p>
    </div>
  );
}
